import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '口碑评价', icon: 'ri-star-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-contacts-line' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '韩国漫画网',
    'url': 'https://www.koreanmanhwa.cn/',
    'description': '韩国漫画网是热门漫画在线阅读平台，全网韩漫最快更新，涵盖韩漫大全，内容多样，加载快捷。',
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': '望京SOHO T1 1208室',
      'addressLocality': '北京',
      'addressRegion': '朝阳区',
      'addressCountry': 'CN',
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': '400-888-6666',
      'contactType': 'customer service',
      'email': 'contact@koreanmanhwa.cn',
      'availableLanguage': ['Chinese'],
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '关于我们 - 韩国漫画网',
    'description': '了解韩国漫画网的品牌故事、用户口碑评价以及联系方式。韩国漫画网致力于为读者提供全网最快更新的韩国漫画在线阅读服务。',
    'url': 'https://www.koreanmanhwa.cn/about',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩国漫画网',
      'url': 'https://www.koreanmanhwa.cn/',
    },
  };

  const reputationData = [
    {
      name: '王小明',
      avatar: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20young%20man%20portrait%2C%20warm%20expression%2C%20simple%20background%2C%20clean%20style&width=100&height=100&seq=avatar-user-01&orientation=squarish',
      rating: 5,
      comment: '这个网站真的超棒！更新速度比我之前用的所有平台都快，画质也很清晰。每天必刷的网站！',
      date: '2026-07-28',
    },
    {
      name: '李小美',
      avatar: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20young%20woman%20portrait%2C%20smiling%20face%2C%20simple%20warm%20background%2C%20clean%20style&width=100&height=100&seq=avatar-user-02&orientation=squarish',
      rating: 5,
      comment: '分类很全，找到了一直想看的冷门韩漫。界面简洁好看，阅读体验非常流畅，强烈推荐给大家！',
      date: '2026-07-25',
    },
    {
      name: '张浩然',
      avatar: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20young%20man%20portrait%20with%20glasses%2C%20friendly%20expression%2C%20simple%20background%2C%20clean%20style&width=100&height=100&seq=avatar-user-03&orientation=squarish',
      rating: 4,
      comment: '作为韩漫老读者，这个网站的排版和阅读体验确实不错。希望后续能加入更多新作品，持续关注中。',
      date: '2026-07-20',
    },
    {
      name: '赵雨晴',
      avatar: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20young%20woman%20portrait%2C%20gentle%20smile%2C%20simple%20warm%20background%2C%20clean%20illustration%20style&width=100&height=100&seq=avatar-user-04&orientation=squarish',
      rating: 5,
      comment: '页面加载超级快！之前在其他网站等半天才能打开，这边基本秒开。收藏了好几部在追的漫画，每天来看更新。',
      date: '2026-07-18',
    },
  ];

  return (
    <div>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <div className="bg-background-100/70 pt-20 pb-8">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-900 font-heading">关于我们</h1>
          <p className="text-sm text-foreground-500 mt-1">了解韩国漫画网的故事与使命</p>
        </div>
      </div>

      <div className="px-4 md:px-6 max-w-7xl mx-auto py-6">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-background-50 text-primary-500 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-xs`}></i>
              </span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'intro' && (
        <section className="px-4 md:px-6 pb-14 max-w-4xl mx-auto">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">品牌故事</h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                韩国漫画网成立于2023年，由一群热爱韩国漫画的资深读者和技术专家共同创立。我们深知韩漫爱好者在寻找高质量、快更新的漫画资源时所面临的困难——碎片化的平台、缓慢的加载速度、不完整的内容，这些问题一直困扰着广大韩漫读者。
              </p>
              <p>
                怀着"让每个人都能轻松享受韩漫"的初心，我们打造了这个集速度、质量、多样性于一体的<strong>韩国漫画在线阅读平台</strong>。我们的团队分布在首尔和北京，全天候关注韩国各大漫画平台的最新动态，力争以最快的速度将最新、最热的<strong>韩国漫画</strong>内容呈现给国内读者。
              </p>
              <h3 className="text-lg font-bold text-foreground-900 font-heading mt-6">我们的使命</h3>
              <p>
                成为中文互联网上<strong>韩漫阅读</strong>的首选平台——不止是提供漫画内容，更是为读者打造一个完整体验的<strong>韩漫大全</strong>社区。从发现好作品、畅快阅读到与同好交流，我们希望每一位访问韩国漫画网的读者都能找到属于自己的快乐。
              </p>
              <h3 className="text-lg font-bold text-foreground-900 font-heading mt-6">核心优势</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <div className="flex gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-flashlight-line"></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">全网最快更新</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">24小时同步韩国最新内容，几乎零时差呈现</p>
                  </div>
                </div>
                <div className="flex gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-stack-line"></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">内容多样丰富</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">涵盖8大分类，数千部韩漫作品持续更新</p>
                  </div>
                </div>
                <div className="flex gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-speed-up-line"></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">极速加载体验</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">先进的加载技术，阅读流畅无卡顿</p>
                  </div>
                </div>
                <div className="flex gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-user-heart-line"></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">用户体验至上</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">无需注册，打开即看，操作简单易用</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'reputation' && (
        <section className="px-4 md:px-6 pb-14 max-w-4xl mx-auto">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground-900 font-heading">用户评价</h2>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-accent-500">4.8</span>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <span className="text-xs text-foreground-400">(2,386条评价)</span>
              </div>
            </div>
            <div className="space-y-4">
              {reputationData.map((item, idx) => (
                <div key={idx} className="p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="flex items-start gap-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                        <span className="text-xs text-foreground-400">{item.date}</span>
                      </div>
                      <div className="flex items-center mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span key={i} className={`w-3 h-3 flex items-center justify-center ${i < item.rating ? 'text-accent-500' : 'text-foreground-200'}`}>
                            <i className="ri-star-fill text-xs"></i>
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'contact' && (
        <section className="px-4 md:px-6 pb-14 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1 space-y-4">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-mail-line"></i>
                </span>
                <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                <p className="text-xs text-foreground-500 mt-1">contact@koreanmanhwa.cn</p>
                <p className="text-xs text-foreground-400 mt-0.5">工作日24小时内回复</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-3">
                  <i className="ri-phone-line"></i>
                </span>
                <h3 className="text-sm font-semibold text-foreground-900">客服热线</h3>
                <p className="text-xs text-foreground-500 mt-1">400-888-6666</p>
                <p className="text-xs text-foreground-400 mt-0.5">周一至周五 9:00-18:00</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-3">
                  <i className="ri-map-pin-line"></i>
                </span>
                <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                <p className="text-xs text-foreground-500 mt-1">北京市朝阳区望京SOHO T1 1208室</p>
              </div>
            </div>

            <div className="md:col-span-2 bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">发送消息</h2>
              <ContactForm />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function ContactForm() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');

      const submitAddr = 'https://readdy.ai/api/form/d9p1jeqqs38sj6jc7hhg';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  if (formState === 'success') {
    return (
      <div className="text-center py-10">
        <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-500">
          <i className="ri-check-line text-2xl"></i>
        </span>
        <h3 className="mt-4 text-base font-semibold text-foreground-900">消息已发送！</h3>
        <p className="mt-1 text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
        <button
          onClick={() => setFormState('idle')}
          className="mt-4 px-5 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          发送新消息
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
          placeholder="请输入消息主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
          placeholder="请输入您的消息（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>
      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="hp-field"
      />
      {formState === 'error' && formError && (
        <p className="text-sm text-primary-500">{formError}</p>
      )}
      <button
        type="submit"
        disabled={formState === 'submitting'}
        className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
      >
        {formState === 'submitting' ? '发送中...' : '发送消息'}
      </button>
    </form>
  );
}